import { SourceTypeApi } from '@shared/datastore/api-typings/messages/messages_types';
import { Action } from '../actions';
import {
  mergeEntities,
  mergeListEntities,
  Path,
  pluckListItemFromRawStoreSlice,
  RawStoreListStateSlice,
  removeEntityById,
  transformObjects,
} from '../helpers';
import {
  transformMessages,
  transformWebsocketMessages,
} from './message.transformers';

export function messagesReducer(
  state: RawStoreListStateSlice<'messages'> = {},
  action: Action,
) {
  switch (action.type) {
    case 'API_FETCH_SUCCESS':
      if (action.payload.type === 'messages') {
        const { result, refList } = action.payload;
        return mergeListEntities<'messages'>(
          state,
          transformObjects(result.messages, transformMessages),
          refList,
          'threadId',
        );
      }

      if (action.payload.type === 'threads') {
        const { result } = action.payload;
        if (result.threads) {
          return result.threads.reduce(
            (s, thread): RawStoreListStateSlice<'messages'> =>
              thread.messages && thread.messages.length > 0
                ? mergeEntities<'messages'>(
                    s,
                    transformObjects(thread.messages, transformMessages),
                    {
                      path: [
                        'messages',
                        thread.messages[0].thread_id.toString(),
                      ],
                    },
                  )
                : s,
            state,
          );
        }
      }
      return state;

    case 'API_PUSH': {
      if (action.payload.type === 'messages') {
        const { object: message, ref, rawRequest } = action.payload;
        return mergeEntities<'messages'>(
          state,
          transformObjects(
            [Number(rawRequest.client_message_id)],
            clientMessageId => message,
          ),
          ref,
        );
      }
      if (action.payload.type === 'threads') {
        const { object: thread } = action.payload;
        if (
          thread.message !== undefined &&
          thread.message.clientMessageId !== undefined
        ) {
          return mergeEntities<'messages'>(
            state,
            transformObjects(
              [Number(thread.message.clientMessageId)],
              clientMessageId => thread.message,
            ),
            {
              path: ['messages', thread.id.toString()],
            },
          );
        }
      }
      return state;
    }

    case 'API_PUSH_SUCCESS': {
      if (action.payload.type === 'messages') {
        const { result: rawMessage, ref } = action.payload;
        const clientMessageId = rawMessage.client_message_id || '';
        return mergeEntities<'messages'>(
          removeEntityById<'messages'>(ref, state, clientMessageId),
          transformObjects([rawMessage], transformMessages),
          ref,
        );
      }

      if (action.payload.type === 'threads') {
        const { result: thread } = action.payload;
        return thread.messages && thread.messages.length > 0
          ? mergeEntities<'messages'>(
              state,
              transformObjects(thread.messages, transformMessages),
              {
                path: ['messages', thread.messages[0].thread_id.toString()],
              },
            )
          : state;
      }
      return state;
    }

    case 'API_PUSH_ERROR': {
      if (action.payload.type === 'messages') {
        const { object: rawMessage, ref } = action.payload;
        const clientMessageId =
          rawMessage.clientMessageId !== undefined
            ? rawMessage.clientMessageId.toString()
            : '';
        return removeEntityById<'messages'>(ref, state, clientMessageId);
      }

      if (action.payload.type === 'threads') {
        const { object: thread } = action.payload;
        const clientMessageId =
          thread.message && thread.message.clientMessageId
            ? thread.message.clientMessageId
            : '';
        return removeEntityById<'messages'>(
          { path: ['messages', thread.id.toString()] },
          state,
          clientMessageId.toString(),
        );
      }
      return state;
    }

    case 'WS_MESSAGE': {
      const { payload } = action;
      if (payload.parent_type === 'messages' && payload.type === 'private') {
        const path: Path<'messages'> = [
          'messages',
          String(payload.data.thread_id),
        ];
        const clientMessageId = String(payload.data.client_message_id || -1);
        const ref = { path };
        return mergeEntities<'messages'>(
          removeEntityById<'messages'>(ref, state, clientMessageId),
          transformObjects([payload], transformWebsocketMessages),
          ref,
        );
      } else if (
        payload.parent_type === 'messages' &&
        payload.type === 'attach'
      ) {
        const path: Path<'messages'> = [
          'messages',
          String(payload.data.thread_id),
        ];
        const messageId = payload.data.message_id;
        const ref = { path };
        return mergeEntities<'messages'>(
          state,
          transformObjects([payload.data.thread_id], (threadId: number) => {
            const message = pluckListItemFromRawStoreSlice(
              state,
              path,
              messageId,
            );
            return message
              ? {
                  ...message,
                  attachments: [
                    ...(message.attachments || []),
                    ...payload.data.filenames,
                  ],
                }
              : {
                  id: payload.data.message_id,
                  attachments: payload.data.filenames,
                  fromUser: payload.data.from_user,
                  message: '',
                  messageSource: <SourceTypeApi>'chat_box',
                  threadId: payload.data.thread_id,
                  timeCreated: payload.data.time,
                };
          }),
          ref,
        );
      }
      return state;
    }

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/es/webapp/src/app/@shared/datastore/message/message.reducer.ts